import type { Application, ApplicationEvent } from "@/lib/api/envoy";
import { stamp } from "./dates";
import { text } from "./payload";

/** The resumes written for a posting, and the one that went. */

export interface WrittenResume {
    resumeId: string;
    at: string;
    /** `keyword` or `model`. */
    tailor: string;
    /** The model's id, when a model wrote it. */
    model: string | null;
    /** The one the application holds now. */
    latest: boolean;
}

/** Every resume written for the application, the newest first. */
export function resumesWritten(
    events: readonly ApplicationEvent[],
    currentResumeId: string | null | undefined,
): WrittenResume[] {
    const written = events
        .filter((event) => event.kind === "TAILORED" && text(event.payload, "resume_id"))
        .map((event) => ({
            resumeId: text(event.payload, "resume_id") as string,
            at: event.occurred_at,
            tailor: text(event.payload, "tailor") ?? "model",
            model: text(event.payload, "model"),
            seq: event.seq,
        }))
        .sort((left, right) => right.seq - left.seq);
    const current = currentResumeId ?? written[0]?.resumeId ?? null;
    return written.map((entry) => ({
        resumeId: entry.resumeId,
        at: entry.at,
        tailor: entry.tailor,
        model: entry.model,
        latest: entry.resumeId === current,
    }));
}

export interface Sent {
    at: string | null;
    /** Who recorded it: `person`. */
    by: string | null;
    throughExtension: boolean;
    /** The tailored resume that went, when one did. */
    resumeId: string | null;
}

/** How the application went in, from the application and its APPLIED event. */
export function sentOf(application: Application, events: readonly ApplicationEvent[]): Sent {
    const applied = [...events].reverse().find((event) => event.kind === "APPLIED");
    return {
        at: application.applied_at ?? applied?.occurred_at ?? null,
        by: application.applied_by ?? text(applied?.payload, "by"),
        throughExtension: text(applied?.payload, "via") === "extension",
        resumeId: application.applied_resume_id ?? text(applied?.payload, "resume_id"),
    };
}

/** "12 Oct, 09:14, by you, through the extension". */
export function sentPhrase(sent: Sent, timeZone?: string, withYear = false): string {
    return [
        sent.at ? stamp(sent.at, withYear, timeZone) : null,
        sent.by === "person" || sent.by === null ? "by you" : `by ${sent.by}`,
        sent.throughExtension ? "through the extension" : null,
    ]
        .filter(Boolean)
        .join(", ");
}
